import { useSuspenseQuery } from '@tanstack/react-query';
import { hostsCountQueryOptions } from '#api/hosts.queries.ts';
import { LIGHTHOUSE_FILTER, RELAY_FILTER } from '#api/hosts.ts';
import { hostKind, type InfraHost } from '#util/host.ts';

/**
 * Reports whether the given lighthouse/relay is the last active (non-blocked) one on its network —
 * acting on it would leave the network without that role, which is what the block/delete "last one"
 * warnings gate on. Always false for an already-blocked host: blocking or deleting it removes
 * nothing active, so no caller should warn.
 *
 * Per-network: connectivity is per-network, so the warning must be too. Uses the server-side count
 * filtered to non-blocked, so it's exact regardless of host count (no pagination caveat) and fetches
 * only metadata. Suspense, like the other reads in these dialogs.
 */
export function useActiveCount(host: InfraHost) {
  const filter = hostKind(host) === 'relay' ? RELAY_FILTER : LIGHTHOUSE_FILTER;
  const { data: activeCount } = useSuspenseQuery(
    hostsCountQueryOptions({ ...filter, networkID: host.networkID, isBlocked: false })
  );

  return { isLastActive: !host.isBlocked && activeCount < 2 };
}
